import { Injectable, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { from } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { first, map, switchMap } from 'rxjs/operators';
import {
  AutomationsStatus,
  CategoriesGroup,
  Category,
  Currencies,
  Event,
  EventCreateOptions,
  EventStatus,
  IRodrigueEvent,
  Organizer,
  OrganizerAccessRight,
  PayoutPolicy,
  Platforms,
  TicketTemplate,
  TicketingPlatform,
  defaultCategoriesGroup,
  defaultCategory,
} from '../../../models';
import { UploaderService } from '../../../services';
import { createTicketTemplate } from '../../../shared-dashboard/pdfmake';
import { Utils } from '../../../shared/utils';
import { PdfUtils } from '../../../shared/utils/pdf-utils';
import { PartnerUtils } from './utils';

@Injectable({
  providedIn: 'root',
})
export class RodrigueEventService {
  private uploaderService = inject(UploaderService);
  private http = inject(HttpClient);

  configUrl = environment.apiUrl;

  private getRodrigueEvents(organizerId: number) {
    const route = `${this.configUrl}w/a/getRodrigueEvents/${organizerId}/events`;
    return this.http.get<{rodrigueEvents: IRodrigueEvent[], reelaxEvents: Event[]}>(route).pipe(
      first(),
    );
  }

  public loadEvents(options: EventCreateOptions) {
    if (!options.organizer || !options.organizer.id) {
      throw new Error('Rodrigue event load requires organizer id');
    }
    return this.getRodrigueEvents(options.organizer.id)
      .pipe(
        map( (events) => events?.rodrigueEvents?.map(
          (e) => this.mapRodrigueToReelaxEvent(
            e,
            events.reelaxEvents,
            options.eventTemplate,
            options.priceScale,
            options.defaultStatus,
            options.includeFees,
          ),
        )),
      );
  }

  private mapRodrigueToReelaxEvent(
    rodrigueEvent: IRodrigueEvent,
    reelaxEvents: Event[],
    eventTemplate: Event,
    priceScale: number,
    defaultStatus: EventStatus,
    includeFees: boolean,
  ) {
    const newEventDateStart = new Date(rodrigueEvent.session_date);
    const event = {
      ...eventTemplate,
      id: reelaxEvents.find(
        (r) => r.TicketingPlatforms.some((t) => t.platformEventId === `${rodrigueEvent.event_id}@${rodrigueEvent.session_id}`),
      )?.id,
      name: rodrigueEvent.event_name,
      organizerName: eventTemplate?.organizerName ?? rodrigueEvent.organizer.name,
      location: rodrigueEvent.place_name,
      dateStart: newEventDateStart,
      resaleEnd: PartnerUtils.computeDateRelativeTo(
        eventTemplate?.resaleEnd,
        newEventDateStart,
        eventTemplate?.dateStart,
      ),
      dateEnd: undefined,
      address: rodrigueEvent.place_name,
      isPartner: true,
      allowPayinRefund: true,
      status: defaultStatus ? defaultStatus : EventStatus.Online,
      reelaxEvent:  reelaxEvents.find(
        (r) => r.TicketingPlatforms.some((t) => t.platformEventId === `${rodrigueEvent.event_id}@${rodrigueEvent.session_id}`),
      ),
      Organizers: eventTemplate?.Organizers?.length >= 1 ? eventTemplate?.Organizers : [new Organizer({
        ...rodrigueEvent.organizer,
        EventOrganizer: {
          accessRight: OrganizerAccessRight.Owner,
        },
      })],
      TicketingPlatforms: [new TicketingPlatform({
        platform: Platforms.Rodrigue,
        payoutPolicy: PayoutPolicy.Auto,
        platformOrganizerId: rodrigueEvent.organizer.id?.toString(),
        platformEventId: rodrigueEvent.event_id?.toString() + '@' + rodrigueEvent.session_id?.toString(),
      })],
      Categories: rodrigueEvent.prices.map( (price, i) => {
        const catPrice = includeFees ? price.amount : price.amountHC;
        return new Category({
          ...defaultCategory,
          ...(eventTemplate?.Categories?.[0]),
          id: undefined,
          eventId: undefined,
          available: price.amount > 0,
          currency: Currencies.Euro,
          expiryDate: price.seance_date ? new Date(price.seance_date) : undefined,
          modifiedBy: undefined,
          name: price.price_name,
          price: Math.round(catPrice * 100),
          minimalSalePrice: priceScale ? Math.round(catPrice * priceScale * 100) : Math.round(catPrice * 50),
          maximalSalePrice: Math.round(catPrice * 100),
          platformsForeignIds: {[Platforms.Rodrigue]: price.category_id?.toString() + '+' + price.price_id?.toString() + '@' + price.session_id?.toString()},
          categoriesGroupId: price.category_id?.toString() as any,
          order: i,
        });
      }),
      CategoriesGroups: rodrigueEvent.categories.map( (cat, i) => new CategoriesGroup({
        ...defaultCategoriesGroup,
        modifiedBy: undefined,
        name: cat.category_name,
        order: i,
        platformsForeignIds: {[Platforms.Rodrigue]: cat.category_id?.toString()},
      })),
      PostalAddress: {
        address1: rodrigueEvent.place_street1,
        address2: [rodrigueEvent.place_street2, rodrigueEvent.place_street3, rodrigueEvent.place_street4].filter((x) => !!x && x !== '')?.join(', '),
        city: rodrigueEvent.place_city,
        zip_code: rodrigueEvent.place_zip,
        name: rodrigueEvent.place_name,
      },
      Automations: eventTemplate?.Automations?.map( (automation) => {
        const oldAutoTime = new Date(automation.time);
        const templateDateStart = automation?.eventDateStart ?
          new Date(automation?.eventDateStart) : new Date(eventTemplate?.dateStart);
        const newTime = new Date(
          newEventDateStart.getTime() - (templateDateStart.getTime() - oldAutoTime.getTime()),
        ).toString();
        return {
          status: AutomationsStatus.Created,
          task: automation.task,
          time: newTime,
          type: automation.type,
        };
      }),
      WaitListConfig: eventTemplate?.WaitListConfig ?? undefined,
      EventPersonalization: eventTemplate?.EventPersonalization ?? undefined,
    };
    // We merge categories which are the same but divided into 2 categories on Rodrigue.
    // Example : "Tarif Plein" and "Tarif Plein WEB"
    const catToRemove = [];
    event.Categories?.forEach((cat) => {
      // WEB category start or end with WEB. It also has a space after of before the next/previous word.
      if (/^WEB\s|\sWEB$/i.test(cat.name)) {
        const orignalCat = event.Categories.find((otherCat) =>
          cat.price === otherCat.price &&
          cat.categoriesGroupId === otherCat.categoriesGroupId &&
          cat.name.replace(/^WEB\s|\sWEB$/i, '')?.toLowerCase()?.trim() === otherCat.name?.toLowerCase()?.trim(),
        );
        if (orignalCat) {
          // need to merge Rodrigue platformsForeignIds
          orignalCat.platformsForeignIds[Platforms.Rodrigue] =
            `${orignalCat.platformsForeignIds[Platforms.Rodrigue]};${cat.platformsForeignIds[Platforms.Rodrigue]}`;
          catToRemove.push(cat);
        }
      }
    });
    // Once merge is done, we remove duplicates WEB categories
    event.Categories = event.Categories?.filter((cat) => !catToRemove.includes(cat));
    return event;
  }

  public createEventTicketTemplate(event: Event, document: Document, options: EventCreateOptions) {
    const removeDateFromEnd = (inputString) => {
      // Utilise une expression régulière pour rechercher le motif à la fin de la chaîne
      const regex = /\s\d{2}\/\d{2}\/\d{2}\s\d{2}:\d{2}$/;
      // Utilise la méthode replace pour supprimer le motif
      const result = inputString.replace(regex, '');
      return result;
    };

    const eventUrl = 'rod-' + Utils.stringToURLallowed(event.name);
    const textLines: string[] = [
      event.organizerName,
      removeDateFromEnd(event.name),
      new DatePipe('fr-FR').transform(
        event.dateStart,
        'EEEE d MMMM y HH:mm',
      ),
      event.PostalAddress.name,
      [
        event.PostalAddress.address1,
        event.PostalAddress.address2,
        event.PostalAddress.zip_code,
        event.PostalAddress.city,
      ].filter( (a) => !!a).join(', '),
      event.EventPersonalization?.ticketTemplateInformation,
    ];

    const reelaxTicketMask = {
      x: 75, // old template was 44
      y: 78, // old template was 43
      h: 156, // little less than manual because of zoom offset
      w: 200,
    };

    return from(createTicketTemplate([
      {
        alignment: 'center',
        fontSize: 12,
        margin: [0, 0, 0, 0],
        text: textLines[0],
      },
      {
        alignment: 'center',
        fontSize: 12,
        margin: [0, 5, 0, 0],
        text: textLines[1],
        bold: true,
      },
      {
        alignment: 'center',
        fontSize: 12,
        margin: [0, 5, 0, 0],
        text: textLines[2],
        bold: true,
      },
      {
        alignment: 'center',
        fontSize: 12,
        margin: [0, 5, 0, 0],
        text: textLines[3],
        bold: true,
      },
      {
        alignment: 'center',
        fontSize: 12,
        margin: [0, 5, 0, 0],
        text: textLines[4],
      },
      {
        alignment: 'center',
        fontSize: 12,
        margin: [0, 5, 0, 0],
        text: textLines[5],
      },
    ], reelaxTicketMask).then( (pdfBlob) => PdfUtils.pdfToImageDataURLAsync(Utils.blobToFile(pdfBlob, eventUrl + '.jpeg'), document))).pipe(
      switchMap( (convertedPdf) => {
        const image = Utils.blobToFile(Utils.base64ToBlob(convertedPdf[0].base64), eventUrl + '.jpeg');

        return this.uploaderService.postTicketTemplate(image, eventUrl);
      }),
      map( (templateImgUrl) => {
        const template = new TicketTemplate({
          // first default values
          organizerLegalName: event.organizerName,
          bigSeatingInfo: true,
          isLandscape: false,
          bigCode128: false,
          bigCategory: false,
          printerVersion: 3,
          name: 'default',
          // 2nd use template informations
          ...options.eventTemplate?.TicketTemplates?.[0],
          // 3rd set own values
          modifiedBy: undefined,
          templateUrl: templateImgUrl.downloadUrl,
          codeXCoordinate: reelaxTicketMask.x,
          codeYCoordinate: reelaxTicketMask.y,
        });
        return template;
      }),
    );

  }

}
